import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FieldType } from '@ngx-formly/core';
import { finalize, switchMap } from 'rxjs';
import { FormHelper } from 'app/features/admin/modules/features/form-builder/form.util';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { DocumentFileActionsService } from '../../services/document-file-actions.service';
import { openHistoryDialog } from '../history/history.component';
import { DocumentCardComponent } from '../documents/document-card.component';
import type { DocumentCardMetaRow, DocumentCardVariant } from '../documents/document-card.types';
import { formatDisplayValue } from './review-value.util';
import type {
  DocumentCardFile,
  DocumentCardMenu,
  DocumentCardPrimaryAction,
  DocumentCardUploadMode,
} from '../documents/document-card.types';
import { normalizeAcceptedTypes, resolveMaxFileSizeMb, validateUploadFiles } from '../upload/upload-validation.util';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { ModalService, runnerDialogRef } from '../../services/modal.service';
import {
  fieldRulesForDocumentTemplate,
  templateDemands,
  TEMPLATE_METADATA_REQUIREMENTS,
} from '../documents/document-template-field-rules';
import type { CreateRunnerFieldRules } from '../create-form-modal.types';
import { UserStore } from 'app/core/services/user.store';
import { UserRolesStore } from 'app/features/admin/user-roles/user-roles.store';
import { UtilityService } from '../../services/utility.service';
import {
  ONBOARDING_TEMPLATE_FILTER,
  ONBOARDING_TEMPLATE_MODULE_CODE,
  onboardingTargetRoleIds,
  templateMatchesTargetRoles,
} from 'app/shared/utils/onboarding-checklist-templates';
import { debugLog } from '../../utils/debug-logger';
import { anchorEntryOf } from './anchor-entry.util';
import { clearableIsReadOnly } from './clearable-field.type';
import { markUserEdit } from './user-edit.util';
import { WizardHostContext } from './wizard-host-context.service';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { DocumentsDisplayModeEnum } from './required-documents-checklist.types';
import { NoticeSeverityEnum } from '../notice-chip.types';
import { noticeRegistryOf, noticeSourceKeyForField } from '../notice-registry.util';
import { liveEntrySourceOf } from './readonly/display-context.util';
import type { RequiredDocumentsChecklistProps } from './required-documents-checklist.types';
import {
  entryHasFile,
  resolveDocumentFileName,
  resolveDocumentTitle,
  resolveInlineFileMeta,
} from '../../utils/document-file-meta';
import {
  deriveCardBadge,
  deriveDocumentPresentation,
  documentNotificationLeadTime,
  documentReplaceResetValues,
  documentReviewLabel,
  documentValidityLabel,
  type DocumentCardBadge,
  type DocumentPresentation,
  type DocumentReviewState,
} from '../../utils/document-card-status';

interface ChecklistRow {
  template: Entry;
  uploaded: Entry | null;
}

/**
 * Per-document snapshot inside the checklist's own field value: what the host form persists for
 * this field, and what the wizard validator and the cards read back.
 *
 * FE-derived from the template and record data, and NOT mirrored onto the onboarding entry — the
 * Goedkeuren interface action reads `flagCount` off the review field, which is that field's own
 * writer (see the value-sync effect in the constructor). An earlier version mirrored
 * `pendingRequiredDocumentsCount` for a Goedkeuren condition that no longer exists.
 */
interface ChecklistDocument {
  templateId: string;
  templateName: string;
  templateDescription?: string;
  entityType?: 'company' | 'user' | string;
  optional: boolean;
  vereisEinddatum: boolean;
  vereisDocumentnummer: boolean;
  vereisAantalVoertuigen: boolean;
  uploadedId: string | null;
  status: DocumentReviewState;
  hasFile: boolean;
}

interface ChecklistValue {
  requiredDocuments: ChecklistDocument[];
  optionalDocuments: ChecklistDocument[];
  pendingRequiredDocumentsCount: number;
}

// Onboarding defaults — required-documents-checklist was originally authored
// for the document-onboarding flow (#19108). All of these are now genuine,
// consumed prop defaults (see `RequiredDocumentsChecklistProps`) rather than
// silent fallbacks — a second consumer (e.g. the user-scoped "Documenten"
// page, #20619) sets the props explicitly instead of relying on them.
const SOURCE_FIELD = 'entry.template-source'; // not configurable via form-builder
/** Uploads module: pure file storage; records reference it through `bestand`. */
const UPLOADS_MODULE_CODE = 'documents';

/**
 * Host-entry kinds a records-module entry can anchor to via `moduleLinkedField` — the back-ref
 * key that names the field on the record pointing back at the host. `company`/`user` are the
 * original two (bedrijf-/gebruikers-detail); `asset` (bedrijfsmiddel-detail) is the third.
 */
type AnchorKey = 'company' | 'user' | 'asset';

/** The OData clause that scopes the instance-module load to records anchored on this host. */
function anchorClause(key: AnchorKey, id: string): string {
  return `entry.${key}.id eq '${id}'`;
}

const DEFAULTS = {
  moduleCode: 'document-records', // Instance target: document records (status source), not the Uploads module.
  moduleLinkedField: 'entry.entryId', // back-ref field on instance-target pointing to current module
  enforceUploadValidator: true,
  templateModuleCode: ONBOARDING_TEMPLATE_MODULE_CODE,
  templateFilter: ONBOARDING_TEMPLATE_FILTER,
  // The records module is homogeneous and contains only document records, so
  // hoofdtype scoping is unnecessary and harmful for records with a null hoofdtype.
  instanceHoofdtypeSlug: null as string | null,
} as const;

/**
 * Zelfde melding als `documents-cards`: beide oppervlakken openen hetzelfde aanmaakformulier van
 * de records-module, dus een ontbrekende `createFormId` moet er identiek uitzien.
 */
const NO_CREATE_FORM_MESSAGE =
  'Er is nog geen aanmaakformulier geconfigureerd. Vraag je beheerder een aanmaakformulier op de Documenten-module in te stellen.';

/** Zonder de records-module is er geen doel om een document in aan te maken. */
const MISSING_RECORDS_MODULE_MESSAGE =
  'Uploaden is nu niet mogelijk — de documentenmodule ontbreekt of is uitgeschakeld.';

@Component({
  selector: 'formly-required-documents-checklist',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, DocumentCardComponent],
  template: `
    <section class="flex flex-col gap-6">
      @if (props.label) {
        <h3 class="m-0 text-[1.0625rem] font-semibold leading-snug tracking-tight text-default">
          {{ props.label }}
        </h3>
      }
      <!-- Fill-in help is for the edit view; a read row never shows it either (#21239). -->
      @if (props.description && !isReadView()) {
        <p class="m-0 max-w-[64ch] text-sm leading-relaxed text-default">{{ props.description }}</p>
      }

      @if (isLoading()) {
        <div class="flex flex-col gap-4" aria-busy="true" aria-live="polite">
          <div class="h-40 rounded-lg rdc-shimmer"></div>
          <div class="h-40 rounded-lg rdc-shimmer rdc-shimmer--delay-1"></div>
          <span class="sr-only">Documenten laden…</span>
        </div>
      } @else if (rows().length === 0) {
        <div class="rdc-empty flex flex-col items-center gap-2 rounded-lg px-6 py-12 text-center">
          <span
            class="mb-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/5 text-accent dark:bg-white/5"
            aria-hidden="true">
            <mat-icon class="icon-size-5" svgIcon="heroicons_outline:document-text"></mat-icon>
          </span>
          <p class="m-0 text-[0.9375rem] font-semibold text-default">Nog geen vereiste documenten</p>
          <p class="m-0 text-[0.8125rem] text-accent">Vraag je beheerder om documenttemplates toe te voegen.</p>
        </div>
      } @else {
        <!-- Eén kaart per rij: de checklist staat in een formulierkolom (bedrijf → vereiste
             documenten, onboarding-wizard), waar drie kaarten naast elkaar te krap worden. Het
             documents-cards-overzicht heeft de volle paginabreedte en houdt zijn grid. -->
        <ul class="m-0 flex list-none flex-col p-0" [class]="listGapClass()" role="list">
          @for (row of rows(); track row.template.id) {
            <li [class]="rowChromeClasses()">
              <app-document-card
                [variant]="cardVariant()"
                [title]="rowTitle(row)"
                [description]="cardDescription(row)"
                [badge]="rowBadge(row)"
                [showBadge]="showCardBadge(row)"
                [reviewComment]="cardReviewComment(row)"
                [notice]="cardNotice(row)"
                [file]="cardFile(row)"
                [meta]="cardMeta(row)"
                [menu]="cardMenu(row)"
                [primaryAction]="cardPrimaryAction(row)"
                [uploadMode]="cardUploadMode(row)"
                [accept]="rowAcceptedTypes(row).join(',')"
                [dropzoneHint]="rowHint(row)"
                [required]="!rowIsOptional(row)"
                [optional]="rowIsOptional(row)"
                [disabled]="isDisabled()"
                [busy]="uploadingFor() === row.template.id"
                [dataCy]="'document-row-' + row.template.id"
                (filesSelected)="onFilesSelected(row, $event)"
                (preview)="previewRow(row)"
                (download)="downloadRow(row)"
                (history)="historyRow(row)"
                (archive)="archiveRow(row)"
                (example)="downloadTemplate(row.template)"
                (complete)="openCompleteRunner(row)"
                (upload)="openUploadRunner(row)" />
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: `
    :host {
      display: block;
      /* RGB triplet (not a colour literal) so the shimmer tint can flip via one
         rgba(var(...)) instead of duplicating the whole gradient per scheme. */
      --rdc-shimmer-rgb: 15, 23, 42; /* dark navy tint, same hue as --dd-focus-ring's light value */
    }
    :host-context(.dark) {
      --rdc-shimmer-rgb: 255, 255, 255;
    }

    /* Skeleton shimmer — keyframes can't be replaced by Tailwind utilities */
    .rdc-shimmer {
      background: linear-gradient(
        90deg,
        rgba(var(--rdc-shimmer-rgb), 0.04) 0%,
        rgba(var(--rdc-shimmer-rgb), 0.08) 40%,
        rgba(var(--rdc-shimmer-rgb), 0.04) 80%
      );
      background-size: 200% 100%;
      animation: rdc-shimmer 1.4s ease-in-out infinite;
    }
    .rdc-shimmer--delay-1 {
      animation-delay: 0.15s;
    }
    .rdc-shimmer--delay-2 {
      animation-delay: 0.3s;
    }
    @keyframes rdc-shimmer {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }

    /* Plain-CSS borders: Tailwind's border utilities are clamped to 12% opacity under .dark. */
    .rdc-empty {
      border: 1px dashed var(--dd-card-border);
      background-color: rgba(0, 0, 0, 0.015);
    }
    :host-context(.dark) .rdc-empty {
      background-color: rgba(255, 255, 255, 0.02);
    }
  `,
})
export class FormlyRequiredDocumentsChecklistComponent extends FieldType implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #userStore = inject(UserStore);
  readonly #userRolesStore = inject(UserRolesStore);
  readonly #utilityService = inject(UtilityService);
  readonly #documentActions = inject(DocumentFileActionsService);
  readonly #dialog = inject(MatDialog);
  readonly #destroyRef = inject(DestroyRef);
  readonly #modalService = inject(ModalService);
  /** The host's half of the host ↔ wizard channel — see {@link WizardHostContext}. `null` outside a hosted wizard/status page. */
  readonly #hostContext = inject(WizardHostContext, { optional: true });

  /**
   * The entry documents anchor to — see {@link anchorEntryOf} for why `formState.entry` alone is
   * not enough on the query-param path (admin hand-over), where it degrades to a shell without an
   * `id`/`entry.user`/`entry.company`.
   *
   * Kept as a plain method for the same reason the helper is a plain function: `options.formState`
   * is a mutable plain object the host may reassign wholesale (e.g. republishing
   * `formState.onboardingForm`, which `#syncTargetRoleIds()` below handles by holding a real
   * `signal`). This is a cheap object walk; call it fresh wherever the current anchor is needed.
   */
  anchorEntry(): Entry | null {
    return anchorEntryOf(this.options?.formState);
  }

  /**
   * Reactive counterpart of `anchorEntry()?.id`, used ONLY to trigger the initial upload load (see
   * the constructor's effect below) — `anchorEntry()` itself stays the plain, always-fresh method
   * every synchronous call site (`canReplace`, `refreshUploaded`, …) already relies on.
   *
   * `anchorEntry()` reads `options.formState` as a plain object, so a ONE-SHOT call in `ngOnInit`
   * can miss a not-yet-loaded entry: on a hard reload of `/…/bewerken`, `fields()` (and this field)
   * can mount before `DetailsService.entry()` has resolved a real entry, and nothing ever re-asked
   * afterwards — every document then read as permanently "ontbreekt". Mirrors
   * `FormlyLinkedItemsComponent.linkedEntryId`: prefer the LIVE `detailsService.formState()`/
   * `entry()` signal over the frozen `options.formState` snapshot, so the id updates once the real
   * entry arrives; fall back to the plain snapshot (`anchorEntryOf`) for hosts without a
   * `DetailsService` (the query-param admin hand-over route), where there is nothing to react to.
   */
  readonly #anchorEntryId = computed<string | undefined>(() => {
    const liveEntrySource = liveEntrySourceOf(this.options?.formState);
    if (liveEntrySource) {
      return liveEntrySource.entry()?.id;
    }
    return anchorEntryOf(this.options?.formState)?.id ?? undefined;
  });

  readonly templates = signal<Entry[]>([]);
  readonly isLoading = signal<boolean>(true);
  /** Template-id waarvoor de bestandsopslag loopt; de kaart toont zolang geen sleepzone. */
  readonly uploadingFor = signal<string | null>(null);

  /**
   * Scope-filter — gezet in ngOnInit vanuit `formState.entry`. Drijft de
   * `uploaded`-computed die de store filtert; signal zodat reactivity werkt.
   */
  readonly #anchorCompanyId = signal<string | null>(null);
  readonly #anchorUserId = signal<string | null>(null);
  readonly #anchorAssetId = signal<string | null>(null);

  /**
   * Uploaded docs reactief gelezen uit `modulesStore.entries()[docModuleId]`.
   * `SignalRService` voedt deze store automatisch bij elke server-side mutatie
   * (admin keurt doc goed → SignalR-event → `modulesStore.updateEntryState` →
   * deze computed re-evaluates → UI rendert nieuwe status). Geen lokale signal
   * meer + geen direct SignalR-subscribe nodig.
   */
  readonly uploaded = computed<Entry[]>(() => {
    const docModuleId = this.instanceModule()?.id;
    if (!docModuleId) return [];
    const companyId = this.#anchorCompanyId();
    const userId = this.#anchorUserId();
    const assetId = this.#anchorAssetId();
    if (!companyId && !userId && !assetId) return [];
    const all = this.#modulesStore.entries()[docModuleId] ?? [];
    const hoofdtypeSlug = this.resolvedInstanceHoofdtypeSlug();
    const filtered = all.filter(e => {
      if (e.entry?.['archived'] === true) return false;
      // Documents-module bevat ook andere doc-types voor dezelfde company/user
      // (contracten, verzuim-attachments, …) — alleen onboarding-documenten
      // belong in the checklist and the pending count. `instanceHoofdtypeSlug: null`
      // (prop) skipt deze clausule volledig voor consumers zonder hoofdtype-scoping.
      if (hoofdtypeSlug && e.entry?.['hoofdtype']?.slug !== hoofdtypeSlug) return false;
      const eCompanyId = e.entry?.['company']?.id;
      const eUserId = e.entry?.['user']?.id;
      const eAssetId = e.entry?.['asset']?.id;
      return (
        (!!companyId && eCompanyId === companyId) ||
        (!!userId && eUserId === userId) ||
        (!!assetId && eAssetId === assetId)
      );
    });
    return filtered;
  });

  readonly templateModule = computed(() => {
    const code = this.resolvedTemplateModuleCode();
    return this.#modulesStore.enabledEntities().find(m => m.code === code);
  });

  readonly instanceModule = computed(() => {
    const code = this.resolvedInstanceModuleCode();
    return this.#modulesStore.enabledEntities().find(m => m.code === code);
  });

  /**
   * Roles this onboarding leads to, declared on the onboarding-form entry. During onboarding the
   * user still holds the interim "Onboarding" role, so filtering on their CURRENT roles would show
   * nothing — the target role is what decides which documents apply. Not a prop: read from
   * `formState.onboardingForm`, published by the hosting onboarding page (see
   * `RequiredDocumentsChecklistProps`).
   *
   * A real `signal`, NOT a plain method reading `formState` on every call — `#visibleTemplates`
   * below stays a genuine `computed()` this way, same pattern as `#anchorCompanyId`/
   * `#anchorUserId` above. Synced explicitly via `#syncTargetRoleIds()` — `formState.onboardingForm`
   * is republished onto a brand-new `options.formState` object out-of-band once the onboarding form
   * resolves over HTTP, so nothing here can `computed()` its way to it.
   */
  readonly #targetRoleIds = signal<string[]>([]);

  /**
   * Re-derive `#targetRoleIds` from the current `formState.onboardingForm` and write it only when
   * the content actually changed (array-content compare, not reference) — otherwise every call
   * would churn `#visibleTemplates`/`rows`/`value` downstream for no reason. Call this wherever
   * `formState` could just have been reassigned with a newly-resolved onboarding form: on mount
   * (`ngOnInit`), whenever uploaded documents are (re)loaded (`loadUploaded`, and transitively
   * `refreshUploaded`), and on every Formly `fieldChanges` emission (see `ngOnInit`) so a
   * late-arriving onboarding form is picked up during normal wizard interaction without requiring
   * the user to trigger a refresh first.
   */
  #syncTargetRoleIds(): void {
    const formState = this.options?.formState as Record<string, unknown> | undefined;
    const next = onboardingTargetRoleIds(formState?.['onboardingForm'] as Entry | undefined);
    const current = this.#targetRoleIds();
    const unchanged = current.length === next.length && current.every((id, i) => id === next[i]);
    if (unchanged) return;
    this.#targetRoleIds.set(next);
  }

  /**
   * Plain method, not `computed()`: it reads `#targetRoleIds()` and `userRolesStore.userRoles()`,
   * both real signals, so any `computed()` calling it (e.g. `#visibleTemplates`) tracks them.
   * The rule itself — including "no target roles means no filtering" — lives in
   * `onboarding-checklist-templates.ts`, shared with the reviewer's side.
   */
  templateMatchesTargetRoles(template: Entry): boolean {
    return templateMatchesTargetRoles(
      template,
      this.#targetRoleIds(),
      this.#userRolesStore.userRoles() ?? [],
      UtilityService.createSlug
    );
  }

  /**
   * Templates actually visible to this onboarding flow: loaded set (already scoped server-side by
   * `templateFilter`, default `showInOnboarding`) narrowed by target role. Shared between `rows()`
   * and `notifyPendingCount()` so the pending count the wizard reports never counts a template the
   * onboarder can never see or upload.
   */
  readonly #visibleTemplates = computed<Entry[]>(() => this.templates().filter(t => this.templateMatchesTargetRoles(t)));

  readonly rows = computed<ChecklistRow[]>(() => {
    const templates = this.#visibleTemplates();
    const uploaded = this.uploaded();
    const sourceFieldKey = this.unwrapKeyPath(SOURCE_FIELD);
    return templates.map(template => ({
      template,
      uploaded:
        uploaded.find(u => {
          const ref = u.entry?.[sourceFieldKey];
          return ref?.id === template.id;
        }) ?? null,
    }));
  });

  /** Rows for templates that are NOT marked optional — these gate the wizard. */
  readonly requiredRows = computed(() => this.rows().filter(r => !this.rowIsOptional(r)));
  readonly optionalRows = computed(() => this.rows().filter(r => this.rowIsOptional(r)));

  /** Structured value object that becomes the formControl's value. */
  readonly value = computed<ChecklistValue>(() => {
    const toDoc = (row: ChecklistRow): ChecklistDocument => ({
      templateId: row.template.id,
      templateName: row.template.entry?.['name'] ?? '',
      templateDescription: row.template.entry?.['description'] ?? undefined,
      entityType: row.template.entry?.['entityType'],
      optional: this.rowIsOptional(row),
      vereisEinddatum: templateDemands(row.template.entry, 'vereisEinddatum'),
      vereisDocumentnummer: templateDemands(row.template.entry, 'vereisDocumentnummer'),
      vereisAantalVoertuigen: templateDemands(row.template.entry, 'vereisAantalVoertuigen'),
      uploadedId: row.uploaded?.id ?? null,
      status: this.rowPresentation(row).reviewState,
      hasFile: entryHasFile(row.uploaded),
    });

    const requiredDocuments = this.requiredRows().map(toDoc);
    const optionalDocuments = this.optionalRows().map(toDoc);
    const pendingRequiredDocumentsCount = this.requiredRows().filter(row => this.rowBlocksOnboarding(row)).length;

    return { requiredDocuments, optionalDocuments, pendingRequiredDocumentsCount };
  });

  /**
   * True wanneer de host-entry zélf het document-anker is (bedrijf-, gebruikers- of
   * bedrijfsmiddel-detail, via de back-ref-prop) in plaats van een aanmelding/
   * onboarding-host met eigen company/user-velden. Buiten de onboarding-
   * context bestaat er geen wizard-/verzendfase.
   *
   * The `asset` kind is special-cased ahead of the nested-field check: an asset host may carry an
   * owner ref of its own (e.g. `entry.general.company`) without that being what this document is
   * FOR, so the configured back-ref wins unconditionally there. `company`/`user` keep their
   * original precedence — a host that itself carries `entry.company`/`entry.user` is the
   * onboarding-aanmelding case even when `moduleLinkedField` happens to be `'entry.company'`
   * (the wizard's own "Vereiste documenten" config); changing that would turn the onboarding
   * wizard into a management surface (code review, scoped major fix).
   *
   * Plain method, not `computed()` — it transitively reads `anchorEntry()`, which is itself a
   * plain method (see above) for the same zero-producer reason. Wrapping this in `computed()`
   * would freeze it at first read regardless of that fix.
   */
  #hostIsAnchor(): boolean {
    const host = this.anchorEntry();
    if (!host) return false;
    const backRefKey = this.unwrapKeyPath(this.resolvedBackRefPath());
    if (backRefKey === 'asset') return true;
    if (host.entry?.company?.id || host.entry?.user?.id) return false;
    return backRefKey === 'company' || backRefKey === 'user';
  }

  /**
   * Version history and archive are document-MANAGEMENT actions that belong on the company/user
   * document surfaces (where the host entry is the document anchor), not in the onboarding capture
   * flow. Someone doing onboarding uploads and replaces files; they never manage versions or
   * archive. `#hostIsAnchor()` is exactly that management-vs-onboarding distinction, so the template
   * gates those two buttons on this. Public wrapper because the template cannot read the
   * ES-private method directly.
   */
  protected isManagementSurface(): boolean {
    return this.#hostIsAnchor();
  }

  /**
   * True when the parent entry (de onboarding) nog niet verzonden is.
   * Op `concept`-status zit de onboarder nog in de wizard — uploaded docs
   * heten dan "Klaar voor verzending" ipv "In behandeling".
   *
   * Plain method, not `computed()` — same reasoning as `#hostIsAnchor()` and `anchorEntry()`
   * above: it depends on nothing but plain-method reads, so a `computed()` wrapper would freeze
   * at first evaluation and `rowState()` would never observe a submit/status change again.
   */
  isPreSubmit(): boolean {
    // Bedrijf-/gebruikers-detail: docs worden hier post-onboarding beheerd,
    // pre-submit-semantiek ("Klaar voor verzending") is nooit van toepassing.
    if (this.#hostIsAnchor()) return false;
    const slug = this.anchorEntry()?.entry?.status?.slug;
    if (!slug) return true; // geen status bekend → behandel als pre-submit
    return slug === 'concept';
  }

  /**
   * Wizard-validity gate: alleen non-optional templates (`!rowIsOptional(row)`, tolerant voor
   * zowel de boolean als de string-vorm van `entry.optional`) moeten een uploaded file hebben
   * voordat de onboarder verder kan. Optionele certificaten blokkeren Volgende dus niet.
   */
  readonly allRequiredUploaded = computed(() => {
    const required = this.requiredRows();
    if (required.length === 0) return true;
    return required.every(r => entryHasFile(r.uploaded));
  });

  /**
   * Rejected rows across ALL rows — required AND optional. The file is present, so
   * `allRequiredUploaded` (which only looks at required rows) would pass a rejected one, but a
   * rejection is an explicit reviewer demand for a replacement regardless of whether the template
   * is required: resubmitting the same document would walk the carrier straight into a second
   * denial either way. Counted separately from the missing ones so the banner can name each with
   * the right verb.
   */
  readonly #rejectedCount = computed(
    () => this.rows().filter(r => entryHasFile(r.uploaded) && this.#rowIsRejected(r)).length
  );

  /** The rejections that actually hold this surface up — see {@link #rejectionBlocksProgress}. */
  #blockingRejectedCount(): number {
    return this.#rejectionBlocksProgress() ? this.#rejectedCount() : 0;
  }

  /**
   * Whether a rejected document blocks progress. Only in the capture flow: there a submit exists to
   * block, and resending a document the reviewer already turned down buys a second denial. A
   * management surface (`#hostIsAnchor()` — the company/user documents page) has no submit at all;
   * it is an archive where a rejection is a status to read. Blocking there left the hosting form
   * permanently invalid with nothing on the page able to clear it, so that surface keeps behaving
   * exactly as it did before the gate existed.
   *
   * Plain method, like `#hostIsAnchor()` itself. `bannerMessage` below is a `computed()` that reads
   * it: the surface is settled before any row arrives (it derives from the host entry and a prop),
   * and the computed re-evaluates on its first non-empty `rows()`, so there is nothing for it to go
   * stale on.
   */
  #rejectionBlocksProgress(): boolean {
    return !this.#hostIsAnchor();
  }

  readonly bannerMessage = computed<string | null>(() => {
    const total = this.rows().length;
    if (total === 0) return null;
    const blocking: string[] = [];
    const missingRequired = this.requiredRows().filter(r => !entryHasFile(r.uploaded)).length;
    if (missingRequired > 0) {
      blocking.push(
        missingRequired === 1
          ? 'Eén verplicht document moet nog worden geüpload voordat je verder kunt.'
          : `Nog ${missingRequired} verplichte documenten moeten worden geüpload voordat je verder kunt.`
      );
    }
    const rejected = this.#blockingRejectedCount();
    if (rejected > 0) {
      blocking.push(
        rejected === 1
          ? 'Eén afgekeurd document moet opnieuw worden geüpload voordat je verder kunt.'
          : `Nog ${rejected} afgekeurde documenten moeten opnieuw worden geüpload voordat je verder kunt.`
      );
    }
    // Both count as "upload something", so they are stated together rather than one at a time —
    // the carrier otherwise fixes the missing document only to be stopped again by the rejected one.
    if (blocking.length > 0) return blocking.join(' ');
    const missingMetadata = this.rows().filter(r => !this.rowMetadataComplete(r)).length;
    if (missingMetadata > 0) {
      return missingMetadata === 1
        ? 'Eén document mist nog vereiste gegevens (einddatum, documentnummer of aantal).'
        : `${missingMetadata} documenten missen nog vereiste gegevens (einddatum, documentnummer of aantal).`;
    }
    return null;
  });

  /**
   * Reviewer verdict "Afgekeurd", read straight off the row's own record. A pure function of
   * `rows()` on purpose: `rowState()` also consults `isPreSubmit()` (the host entry's status),
   * which a `computed()` cannot track, so the gate below must not depend on that branch.
   */
  #rowIsRejected(row: ChecklistRow): boolean {
    return row.uploaded?.entry?.['status']?.slug === 'rejected';
  }

  rowState(row: ChecklistRow): 'approved' | 'pending' | 'ready' | 'rejected' | 'missing' | 'expired' {
    if (!row.uploaded) return 'missing';
    const slug = row.uploaded.entry?.['status']?.slug;
    if (slug === 'approved') return 'approved';
    if (this.#rowIsRejected(row)) return 'rejected';
    if (slug === 'expired') return 'expired';
    // Geüpload + niet beoordeeld:
    //  - Aanmelding nog niet verzonden → "ready" (klaar voor verzending)
    //  - Aanmelding al verzonden → "pending" (in behandeling)
    return this.isPreSubmit() ? 'ready' : 'pending';
  }

  /** Shared status + expiry derivation used by the standalone /documenten cards. */
  rowBadge(row: ChecklistRow): DocumentCardBadge {
    return deriveCardBadge({
      state: this.rowState(row),
      endDate: row.uploaded?.entry?.['end-date'] ?? null,
    });
  }

  rowPresentation(row: ChecklistRow): DocumentPresentation {
    return deriveDocumentPresentation({
      state: this.rowState(row),
      endDate: row.uploaded?.entry?.['end-date'] ?? null,
      hasFile: entryHasFile(row.uploaded),
      metadataComplete: this.rowMetadataComplete(row),
    });
  }

  rowBlocksOnboarding(row: ChecklistRow): boolean {
    return this.rowPresentation(row).blocksOnboarding;
  }

  rowFileName(row: ChecklistRow): string | null {
    return resolveDocumentFileName(row.uploaded);
  }

  /**
   * Optional-ness tolerates both shapes. The template's `optional` field is a string-toggle in the
   * admin UI (writing 'true'), while seeded and MCP-written templates carry a real boolean. A
   * strict `=== true` check made every UI-edited template behave as required.
   */
  rowIsOptional(row: ChecklistRow): boolean {
    const raw = row.template.entry?.['optional'];
    return raw === true || raw === 'true';
  }

  /**
   * Validator: requires that every required template has a USABLE uploaded file, AND that no
   * row — required or optional — carries a rejected record.
   *
   * `pending`/`ready` do NOT block the wizard — that review loop runs parallel to the
   * "In behandeling" page with its existing pending banner, so the carrier can upload →
   * accept terms → submit without waiting for admin review. `rejected` is the
   * opposite case: the reviewer has already seen and rejected this document, so
   * resubmitting without a replacement is guaranteed to earn a second rejection — that applies
   * to an optional document just as much as a required one: a rejection is always an
   * explicit request for replacement, not something the required/optional distinction can wave away.
   * `expired` stays a warning (no blocking) — that is not a verdict on this document.
   *
   * The rejection dimension only applies in the intake flow; see {@link #rejectionBlocksProgress}.
   */
  private readonly requireAllUploaded: ValidatorFn = (_: AbstractControl): ValidationErrors | null => {
    if (this.isLoading()) return null;
    if (this.rows().length === 0) return null;
    const rejected = this.#blockingRejectedCount();
    if (this.allRequiredUploaded() && rejected === 0 && this.allMetadataFilled()) return null;
    const missingRequired = this.requiredRows().filter(r => !entryHasFile(r.uploaded)).length;
    const missingMetadata = this.rows().filter(r => !this.rowMetadataComplete(r)).length;
    return {
      requiredDocumentsChecklist: {
        message: this.bannerMessage(),
        missing: missingRequired,
        rejected,
        missingMetadata,
      },
    };
  };

  /** True when every uploaded doc whose template flags vereisX has the corresponding field filled. */
  readonly allMetadataFilled = computed(() => this.rows().every(r => this.rowMetadataComplete(r)));

  /**
   * Per-row check: alle template-required metadata-velden moeten gevuld zijn op de uploaded entry.
   * Rows zonder upload zijn niet metadata-incomplete (vallen onder requireAllUploaded).
   */
  private rowMetadataComplete(row: ChecklistRow): boolean {
    return this.#missingMetadataFields(row).length === 0;
  }

  /**
   * Welke template-verplichte metadata-velden nog leeg zijn op de uploaded entry, als
   * gebruikersgerichte labels. Eén implementatie voedt zowel `rowMetadataComplete` (gate) als
   * `cardNotice` (melding), zodat de predicaten niet uit elkaar kunnen lopen.
   */
  #missingMetadataFields(row: ChecklistRow): string[] {
    const up = row.uploaded;
    if (!up) return []; // upload-missing valt onder andere check
    const t = row.template.entry;
    if (!t) return [];
    return TEMPLATE_METADATA_REQUIREMENTS.filter(
      req => templateDemands(t, req.flag) && !req.isFilled(up.entry?.[req.key])
    ).map(req => req.label);
  }

  /**
   * This template's requirements as field rules for the create form. Shared with the
   * Beheer → Documenten overview, which opens the same form: see
   * {@link fieldRulesForDocumentTemplate}.
   */
  #fieldRulesForTemplate(row: ChecklistRow): CreateRunnerFieldRules {
    return fieldRulesForDocumentTemplate(row.template);
  }

  /** Anchor id `loadUploaded()` was last fired for — set by `ngOnInit`'s synchronous check AND the `#anchorEntryId` effect, whichever ran first for a given id. */
  #lastLoadedAanmeldingId: string | undefined;

  constructor() {
    super();

    // Covers the DEFERRED half of the anchor race (#21239 follow-up): an id that only resolves AFTER
    // `ngOnInit` already ran its own synchronous check (see there) — a hard reload of
    // `/…/bewerken`, where this field can mount before `DetailsService.entry()` has settled. That
    // synchronous check already handles the common case (id present at mount) and sets
    // `#lastLoadedAanmeldingId`, so THIS effect's own first run for the SAME id is a harmless
    // no-op — the guard below is shared between both paths. Guarded against ever firing for no id
    // at all (a genuinely new, not-yet-saved entry) the same way `FormlyLinkedItemsComponent`'s own
    // load effect is.
    effect(() => {
      const aanmeldingId = this.#anchorEntryId();
      if (!aanmeldingId || aanmeldingId === this.#lastLoadedAanmeldingId) return;
      this.#lastLoadedAanmeldingId = aanmeldingId;
      // untracked: loadUploaded() reads/writes several of this component's own signals; none of
      // those may become a dependency of THIS effect, or an unrelated signal write would re-enter
      // the guard above.
      //
      // The LIVE entry travels along, not just its id: `loadUploaded()` derives the anchor
      // (company/user) from the entry, and reading that off the `options.formState` snapshot here
      // would make this load depend on `DetailsService`'s own sync effect having run FIRST for the
      // same signal change. Today it does (parent view effect before child view effect), but
      // nothing enforces that order — and with the guard above already set, a snapshot that
      // lagged one tick would leave every document permanently "ontbreekt" with no retry.
      untracked(() => this.loadUploaded(aanmeldingId, liveEntrySourceOf(this.options?.formState)?.entry()));
    });

    // Keep our notice in DetailsService's registry (in-page state, never a server write) in step
    // with our uploaded state. This used to be called from the loadUploaded HTTP callback; now
    // that `uploaded` comes reactively out of the store, it hangs off an effect instead.
    effect(() => {
      // Read all three so the effect re-runs on every change to any of them — `isLoading` is what
      // lets it re-fire the moment templates finish loading, even on a run where `notifyPendingCount`
      // itself skipped (see there).
      this.uploaded();
      this.templates();
      this.isLoading();
      this.notifyPendingCount();
    });

    // Sync the formControl value with the `value` computed. The pending count is NOT mirrored
    // onto the aanmelding any more: the review field owns `entry.<reviewKey>.flagCount` (which
    // the Goedkeuren condition reads) and is its only writer.
    effect(() => {
      this.isLoading(); // tracking
      const value = this.value();
      const ctrl = this.field?.formControl;
      if (!ctrl) return;

      const current = ctrl.value as ChecklistValue | null;
      const sameCount = current?.pendingRequiredDocumentsCount === value.pendingRequiredDocumentsCount;
      const sameRequiredIds =
        Array.isArray(current?.requiredDocuments) &&
        current.requiredDocuments.length === value.requiredDocuments.length &&
        current.requiredDocuments.every((d, i) => d.uploadedId === value.requiredDocuments[i].uploadedId);
      const sameOptionalIds =
        Array.isArray(current?.optionalDocuments) &&
        current.optionalDocuments.length === value.optionalDocuments.length &&
        current.optionalDocuments.every((d, i) => d.uploadedId === value.optionalDocuments[i].uploadedId);

      if (!sameCount || !sameRequiredIds || !sameOptionalIds) {
        /*
          This write is derivation, never a user edit: the summary is rebuilt from server records
          as the templates and uploads arrive, and marking THAT dirty told the host form the
          carrier had changed something before they had touched anything — which is what made a
          resubmit look like a real edit (#21303). `setValue` leaves `pristine` alone, so a
          derived write never hides or fakes an edit; the user's own document work is attributed
          in `#markDocumentsEdited`. DetailsService folds pristine writes into its saved-value
          baseline, so nothing is lost by staying pristine.
        */
        ctrl.setValue(value);
      } else {
        ctrl.updateValueAndValidity();
      }
    });
  }

  /**
   * Record ids the user changed in THIS session (upload, replace, complete, archive). Held as a
   * signal so the rows re-render when one is added — a notice the host published before the change
   * must disappear the moment the change lands, not on the next unrelated re-render.
   */
  readonly #recordsEditedThisSession = signal<ReadonlySet<string>>(new Set());

  /**
   * Attribute a runner result to the user. Called when the create/complete runner closed WITH a
   * result (the modal closes with `{ createdEntries, submission }` after a save and with nothing
   * on cancel), and deliberately not tied to a value change of the summary: replacing a document
   * keeps the same record id, so the derived summary stays identical while the carrier did edit —
   * exactly the case where the wizard must offer "Onboarding indienen" again (#21303).
   *
   * `recordId` is the record the change landed on, when there was one to name beforehand (a
   * replace, a completion, an archive). A brand-new upload has no id yet and needs none: nothing
   * can have published a notice for a record that did not exist.
   */
  #markDocumentsEdited(runnerResult: unknown, recordId?: string): void {
    if (!runnerResult) return;
    if (recordId) {
      this.#recordsEditedThisSession.update(ids => new Set(ids).add(recordId));
    }
    markUserEdit(this.field?.formControl);
  }

  // ── Props resolution ─────────────────────────────────────────────────────
  // Every prop below has a resolution helper so the many read-sites stay
  // consistent and so a default (prop unset) is provably identical to the
  // original onboarding-only constants. See `RequiredDocumentsChecklistProps`
  // for the full contract.

  private resolvedInstanceModuleCode(): string {
    const props = this.props as RequiredDocumentsChecklistProps;
    return props.instanceModuleCode ?? props.moduleCode ?? DEFAULTS.moduleCode;
  }

  private resolvedTemplateModuleCode(): string {
    return (this.props as RequiredDocumentsChecklistProps).templateModuleCode ?? DEFAULTS.templateModuleCode;
  }

  /**
   * Weergave uit de veldconfiguratie. Gewone methode, geen computed: `this.props` is een plat
   * object op de Formly-field-config en geen signal, dus een computed zou nooit hertekenen.
   */
  isRowVariant(): boolean {
    return (this.props as RequiredDocumentsChecklistProps).documentsDisplayMode === DocumentsDisplayModeEnum.Rows;
  }

  /**
   * Figma zet 20px tussen twee regels én tussen twee groepen in "Documents & uploads" (het
   * leesgezicht); de kaartenlijst hield altijd 16px aan.
   */
  listGapClass(): string {
    return this.isRowVariant() || this.isReadView() ? 'gap-5' : 'gap-4';
  }

  /**
   * Which face the shared card draws. The read view (#21239) is the Figma "Documents & uploads"
   * card the onboarding review already uses — `review`: label, status, the compact upload row
   * with its inline preview/download buttons, and the record's metadata as read rows. Nothing that
   * offers a mutation. Edit keeps the management card (or the onboarding row).
   */
  cardVariant(): DocumentCardVariant {
    if (this.isReadView()) return 'review';
    return this.isRowVariant() ? 'row' : 'card';
  }

  /**
   * The record's own metadata, shown under the card in the read view only — the same two rows the
   * onboarding review shows (`readonly-onboarding.component.ts` documentMeta), read off the same
   * records entry. Rows without a value are dropped by the card.
   */
  cardMeta(row: ChecklistRow): DocumentCardMetaRow[] {
    const record = row.uploaded;
    if (!this.isReadView() || !record) return [];
    return [
      { label: 'Documentnummer', value: formatDisplayValue(record.entry?.['document-number']) },
      { label: 'Vervaldatum', value: formatDisplayValue(record.entry?.['end-date']) },
    ];
  }

  /**
   * Rij-ritmiek en scheidingslijn horen bij de lijst, niet bij de kaart: alleen hier is bekend
   * welke regel de laatste is. Figma: 4px boven de inhoud, 8px eronder, dan een hairline in
   * Border/Default; de laatste regel heeft geen lijn en geen onderpadding (rijhoogte 56 → 47).
   */
  rowChromeClasses(): string {
    // Tailwind's scanner leest bronbestanden als tekst: deze string moet letterlijk blijven staan,
    // niet samengesteld worden uit fragmenten.
    return this.isRowVariant() ? 'block border-b border-[var(--dd-card-border)] pb-2 pt-1 last:border-b-0 last:pb-0' : '';
  }

  private resolvedTemplateFilter(): string {
    return (this.props as RequiredDocumentsChecklistProps).templateFilter ?? DEFAULTS.templateFilter;
  }

  /** `undefined` (prop unset) falls back to the onboarding default; explicit `null` disables hoofdtype-scoping. */
  private resolvedInstanceHoofdtypeSlug(): string | null {
    const raw = (this.props as RequiredDocumentsChecklistProps).instanceHoofdtypeSlug;
    return raw === undefined ? DEFAULTS.instanceHoofdtypeSlug : raw;
  }

  /**
   * Back-ref path van instance-entry → host. In de sourceField heet dit
   * `moduleLinkedField`, maar de form-generator (form.service.ts — zie de
   * `linkedField: field.linkedField ? … : field.moduleLinkedField`-mapping)
   * hernoemt het in de gegenereerde Formly-props naar `linkedField`. Lees
   * beide vormen zodat zowel raw sourceFields als generated props werken.
   */
  private resolvedBackRefPath(): string {
    return (
      (this.props['moduleLinkedField'] as string | undefined) ??
      (this.props['linkedField'] as string | undefined) ??
      DEFAULTS.moduleLinkedField
    );
  }

  ngOnInit() {
    this.#warnOnDefaultFallback();
    this.#syncTargetRoleIds();

    // No retract-on-destroy here (#21239 follow-up): the notice registry's lifetime is tied to
    // the PAGE (`DetailsService` clears it when the entry changes), not to any one mount of this
    // field. A tab's content only exists in the DOM while that tab is active
    // (`tabs.component.ts`'s `<ng-template matTabContent>`), so this field is routinely torn down
    // and remounted without the entry changing at all — retracting on every such teardown made the
    // "N document(en) wachten" chip (and the tab badge it feeds) flicker off the moment its tab
    // lost focus, instead of staying valid for the whole page visit.

    // `fieldChanges` is Formly's form-wide bus: it fires on any field's valueChanges plus every
    // hidden-state toggle from expression evaluation, so it fires on essentially all normal wizard
    // interaction (initial default-value patching, navigating steps, typing elsewhere). It's the
    // closest thing to a "something changed, re-derive" heartbeat this field has access to — there
    // is no signal for `options.formState` itself being reassigned by the host. Backstopped by the
    // explicit `#syncTargetRoleIds()` calls here and in `loadUploaded()` for the mount + refresh
    // paths, which cover the common case without needing any interaction at all.
    this.options?.fieldChanges
      ?.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#syncTargetRoleIds());

    // Validator alvast aanhaken zodat de wizard al bij eerste render rekent met onze rules.
    // `enforceUploadValidator` (prop, default true) is een opt-out voor consumers waar
    // de checklist puur informatief is en de form-submit niet mag blokkeren.
    const enforceUploadValidator = this.props['enforceUploadValidator'] ?? true;
    if (enforceUploadValidator && this.field?.formControl) {
      this.field.formControl.addValidators(this.requireAllUploaded);
      this.field.formControl.updateValueAndValidity({ emitEvent: false });
    }

    this.loadTemplates();

    // Fire immediately when the anchor id is ALREADY there at mount — the common case, and the
    // one every non-render unit test that calls `ngOnInit()` directly (no `fixture.detectChanges()`
    // cycle to flush the constructor's effect) exercises. `#lastLoadedAanmeldingId` is set here so
    // the effect's own later run for the SAME id (it always runs at least once) is a harmless no-op
    // rather than a duplicate `loadUploaded` call.
    //
    // A brand-new (not-yet-saved) host entry has no id at all — nothing fires here, only templates
    // show, exactly as before. The `#anchorEntryId` effect in the constructor covers the ONE case
    // this synchronous read cannot: the id resolving AFTER this point (`/…/bewerken` hard reload).
    const immediateId = this.anchorEntry()?.id;
    if (immediateId) {
      this.#lastLoadedAanmeldingId = immediateId;
      this.loadUploaded(immediateId);
    }
  }

  isDisabled(): boolean {
    return !!(this.field.props?.disabled || this.form.disabled);
  }

  /**
   * Page read mode (#21239). The read extension stamps `props.readonly` on every answer leaf, so
   * that flag — not `disabled` — is what says "this is the read view". `disabled` alone also comes
   * from rights and conditions in the EDIT view, where the inert Uploaden button is the agreed
   * face; only the read view drops the mutation affordances and the fill-in help entirely.
   */
  isReadView(): boolean {
    return clearableIsReadOnly(this.props);
  }

  /**
   * Dev-time sanity check: `anchorEntry()` derives from `formState.entry` (the host
   * aanmelding/onboarding-entry), falling back to `formState.hostEntry` on the admin
   * hand-over route. A brand-new (not-yet-saved) host entry is fine — `formState.entry`
   * still exists, just without an `.id` — only a *completely absent* `formState.entry`
   * indicates the hosting form forgot to wire it up at all.
   */
  #warnOnDefaultFallback(): void {
    if (this.options?.formState?.entry) return;
    debugLog(
      'RequiredDocsChecklist',
      'formState.entry is missing. The hosting form must set formState.entry for this field to resolve its company/user anchor.'
    );
  }

  templateFile(template: Entry): { documentId?: string; documentName?: string } | null {
    const file = template.entry?.['file'];
    if (file?.documentId) return file;
    return null;
  }

  canReplace(uploaded: Entry): boolean {
    // Vervangen patcht het bestaande record; zonder schrijfrechten zou de
    // Uploads-entry al bestaan vóór de patch server-side faalt (verweesde upload).
    const moduleId = this.instanceModule()?.id;
    if (!moduleId || !this.#userStore.checkIfHasEditPermission(moduleId)) return false;
    const slug = uploaded.entry?.['status']?.slug;
    const state = slug === 'approved' || slug === 'rejected' || slug === 'expired' ? slug : 'pending';
    const presentation = deriveDocumentPresentation({
      state,
      endDate: uploaded.entry?.['end-date'] ?? null,
      hasFile: entryHasFile(uploaded),
    });
    return presentation.reviewState !== 'approved' || presentation.validityState === 'expiring' || presentation.validityState === 'expired';
  }

  private onboardingStatusSlug(uploaded: Entry): string {
    const row = this.rows().find(candidate => candidate.uploaded?.id === uploaded.id);
    if (row) {
      const presentation = this.rowPresentation(row);
      if (!presentation.blocksOnboarding) return 'approved';
      return presentation.validityState === 'expired' ? 'expired' : presentation.reviewState;
    }

    const slug = uploaded.entry?.['status']?.slug;
    const state = slug === 'approved' || slug === 'rejected' || slug === 'expired' ? slug : 'pending';
    const presentation = deriveDocumentPresentation({
      state,
      endDate: uploaded.entry?.['end-date'] ?? null,
      hasFile: entryHasFile(uploaded),
    });
    if (!presentation.blocksOnboarding) return 'approved';
    return presentation.validityState === 'expired' ? 'expired' : presentation.reviewState;
  }

  // ── Loading ──────────────────────────────────────────────────────────────

  private loadTemplates(): void {
    const moduleId = this.templateModule()?.id;
    if (!moduleId) {
      debugLog('OnboardingDocs', 'checklist: template module not found', this.resolvedTemplateModuleCode());
      this.isLoading.set(false);
      return;
    }

    this.#entriesService
      .getEntries({ moduleId, offset: 0, limit: 200, filter: this.resolvedTemplateFilter() })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (templates: Entry[]) => {
          this.templates.set(templates ?? []);
          this.isLoading.set(false);
        },
        error: err => {
          console.error('Failed to load required-document templates:', err);
          this.isLoading.set(false);
          this.#utilityService.notify('Documenten konden niet worden geladen — probeer de pagina te vernieuwen.');
        },
      });
  }

  /**
   * `liveAnchor`: the entry the caller already holds for `aanmeldingId` (the constructor effect's
   * live `DetailsService.entry()`), used instead of the `options.formState` snapshot when its id
   * matches. The snapshot stays the fallback for the callers that resolved their id FROM that
   * snapshot (`ngOnInit`, `refreshUploaded`), where it is by definition current.
   */
  private loadUploaded(aanmeldingId: string, liveAnchor?: Entry | null): void {
    // Also called from `refreshUploaded()` (after every upload/replace/archive action) — re-sync
    // here so a `formState.onboardingForm` that resolved since the last sync is picked up before
    // this method's own early returns below.
    this.#syncTargetRoleIds();

    const moduleId = this.instanceModule()?.id;
    if (!moduleId) {
      debugLog('OnboardingDocs', 'checklist: instance module not found', this.resolvedInstanceModuleCode());
      return;
    }

    const aanmelding = liveAnchor?.id === aanmeldingId ? liveAnchor : this.anchorEntry();
    const backRefKey = this.unwrapKeyPath(this.resolvedBackRefPath());
    let companyId: string | null = null;
    let userId: string | null = null;
    let assetId: string | null = null;

    if (backRefKey === 'asset') {
      // Asset kind: the configured back-ref wins unconditionally — an asset host may carry an
      // owner ref of its own (e.g. `entry.general.company`) without that leaking into the anchor
      // query. Scoped to `asset` only; company/user keep their original precedence below.
      if (aanmelding?.id) assetId = aanmelding.id;
    } else {
      // Documents live on their natural anchor (company or user). Pick them up via OR
      // on aanmelding's company.id / user.id — so eerder geüploade docs (van vorige
      // onboarding of buiten onboarding context op company-pagina) ook in checklist
      // verschijnen. Vermijdt het uploaden van duplicates per aanmelding.
      companyId = (aanmelding?.entry?.company?.id as string | undefined) ?? null;
      userId = (aanmelding?.entry?.user?.id as string | undefined) ?? null;

      // Host-entry die zélf het anker is (bedrijf- of gebruikers-detail heeft geen eigen
      // entry.company/entry.user): `moduleLinkedField` (bv. 'entry.company' op de bedrijven-tab)
      // wijst dan van instance naar de host — het upload-pad (onFileSelected) schrijft die
      // back-ref al zo, dus de query moet hetzelfde anker gebruiken. Zonder deze fallback blijven
      // bestaande uploads onzichtbaar.
      if (!companyId && !userId && aanmelding?.id) {
        if (backRefKey === 'company') companyId = aanmelding.id;
        else if (backRefKey === 'user') userId = aanmelding.id;
      }
    }

    // Scope-signals voeden de `uploaded` computed die de store filtert.
    this.#anchorCompanyId.set(companyId);
    this.#anchorUserId.set(userId);
    this.#anchorAssetId.set(assetId);

    const anchorClauses: string[] = [];
    if (companyId) anchorClauses.push(anchorClause('company', companyId));
    if (userId) anchorClauses.push(anchorClause('user', userId));
    if (assetId) anchorClauses.push(anchorClause('asset', assetId));

    if (anchorClauses.length === 0) {
      debugLog(
        'OnboardingDocs',
        'checklist: aanmelding heeft geen company/user — kan uploads niet laden',
        aanmeldingId
      );
      return;
    }

    // Hoofdtype-filter voorkomt dat niet-onboarding documenten van dezelfde
    // company/user de 200-entry pagelimiet opsouperen. `instanceHoofdtypeSlug: null`
    // (prop) laat deze clausule volledig weg voor consumers zonder hoofdtype-scoping.
    const hoofdtypeSlug = this.resolvedInstanceHoofdtypeSlug();
    const hoofdtypeClause = hoofdtypeSlug ? ` and entry.hoofdtype.slug eq '${hoofdtypeSlug}'` : '';
    const filter = `(${anchorClauses.join(' or ')})${hoofdtypeClause}`;

    // Vul `modulesStore.entries()[moduleId]` via de store. SignalR-events updaten
    // diezelfde store automatisch (`signalr.service.ts:121 → updateEntryState`),
    // dus onze `uploaded`-computed re-renders zonder eigen subscribe.
    this.#modulesStore.loadEntries({
      moduleId,
      offset: 0,
      limit: 200,
      filter,
      applyQueryParams: false,
    });
    // notifyPendingCount triggert na initial load via een effect (zie constructor).
  }

  private refreshUploaded(): void {
    const aanmeldingId = this.anchorEntry()?.id;
    if (aanmeldingId) this.loadUploaded(aanmeldingId);
  }

  // ── Upload via het aanmaakformulier ──────────────────────────────────────

  /**
   * Uploaden, vervangen en opnieuw uploaden lopen alle drie via het aanmaakformulier van de
   * records-module — hetzelfde pad als `documents-cards` (#20856). Dat formulier bezit het
   * upload-veld (inclusief sleepzone en bestandsvalidatie) én de metadata-velden (einddatum,
   * documentnummer, aantal voertuigen), zodat de checklist geen eigen schrijfpad meer heeft dat
   * de formuliervalidatie omzeilt.
   */
  /**
   * Sleepzone op een nog-lege kaart: het bestand gaat meteen naar de Uploads-module en de
   * runner opent mét die verwijzing al ingevuld, zodat de gebruiker in het formulier alleen nog de
   * resterende velden (einddatum, documentnummer, …) hoeft aan te vullen en op te slaan.
   *
   * Het record zelf wordt nog steeds uitsluitend door het aanmaakformulier geschreven — dit pad
   * maakt alleen de bestandsopslag aan. Gevolg: annuleert de gebruiker het formulier, dan blijft er
   * een ongekoppelde Uploads-entry achter; die wordt gelogd als opruimtaak, net als in het oude
   * uploadpad.
   */
  onFilesSelected(row: ChecklistRow, files: FileList): void {
    if (!files?.length || this.isDisabled()) return;

    // De `accept` van de sleepzone filtert alleen de bestandskiezer, niet een drop.
    const validation = validateUploadFiles(files, this.#acceptedTypesFor(row.template), this.#resolvedMaxFileSizeMb());
    if (validation.error) {
      this.#utilityService.notify(validation.error);
      return;
    }

    const uploadsModuleId = this.#modulesStore.moduleCodeToId(UPLOADS_MODULE_CODE);
    if (!uploadsModuleId) {
      // Zonder Uploads-module kan het gesleepte bestand nergens heen; elk ander faalpad meldt
      // zich ook, dus een stille no-op na een drop zou hier de uitzondering zijn.
      this.#utilityService.notify('Uploaden is nu niet mogelijk — de Uploads-module ontbreekt of is uitgeschakeld.');
      return;
    }

    // Poortwachters vóór addEntry (PR-review Joshua, P2): dit zijn de twee misconfiguraties die
    // deterministisch te zien zijn zonder de runner te openen. Zonder deze check zou elke poging
    // in zo'n omgeving een nieuw ongekoppeld bestand achterlaten. Wat hierna alsnog misgaat
    // (formulier zonder wizard/formActions, geen leesrecht) wordt na de upload opgeruimd.
    const recordsModule = this.instanceModule();
    if (!recordsModule) {
      this.#utilityService.notify(MISSING_RECORDS_MODULE_MESSAGE);
      return;
    }
    if (!recordsModule.createFormId) {
      this.#utilityService.notify(NO_CREATE_FORM_MESSAGE);
      return;
    }

    const anchor = this.#resolveAnchorRefs();
    const bakData: Record<string, unknown> = {
      archived: false,
      name: row.template.entry?.['name'] ?? files[0].name,
      user: anchor.user,
      company: anchor.company,
      // Conditional: an `asset` key that PATCH-merges as `null` on every company/user upload
      // (whether the uploads module even carries that field is unverified) would be a needless,
      // unverified write — omit it entirely rather than writing `asset: null`.
      ...(anchor.asset ? { asset: anchor.asset } : {}),
      share: 'private',
      file: { documentName: files[0].name },
    };

    this.uploadingFor.set(row.template.id);
    this.#entriesService
      .addEntry(uploadsModuleId, bakData as Entry['entry'], { fieldKey: 'entry.file', files, isPublic: false })
      .pipe(
        finalize(() => this.uploadingFor.set(null)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: bakEntry => {
          const geopend = this.#tryOpenRunner(row, {
            id: bakEntry.id,
            moduleId: uploadsModuleId,
            documentType: `module_entry_${uploadsModuleId}`,
            selectedInput: files[0].name,
            privateDocuments: bakEntry.privateDocuments ?? [],
          });
          if (!geopend) this.#discardOrphanedUpload(uploadsModuleId, bakEntry.id);
        },
        error: err => {
          console.error('Upload failed:', err);
          this.#utilityService.notify('Uploaden mislukt — probeer opnieuw.');
        },
      });
  }

  /**
   * Template- en testingang. Bewust `void`: Angular roept `preventDefault()` aan wanneer een
   * event-handler `false` teruggeeft, en het `upload`-output draagt geen DOM-event.
   */
  openUploadRunner(row: ChecklistRow, bestand?: Record<string, unknown>): void {
    this.#tryOpenRunner(row, bestand);
  }

  /**
   * Opent de runner en meldt of dat gelukt is. `onFilesSelected` gebruikt die uitkomst om een
   * zojuist opgeslagen sleepzone-bestand op te ruimen wanneer de runner níét opent (PR-review
   * Joshua, P2) — zonder de beslislogica van `ModalService` hier te dupliceren.
   *
   * `pending` telt als geopend: de runner-retry in `ModalService` opent alsnog zodra de
   * form-module geladen is. Blijft die retry uiteindelijk falen, dan meldt ModalService dat zelf
   * en blijft het bestand achter — bekend restrisico, niet stil.
   */
  #tryOpenRunner(row: ChecklistRow, bestand?: Record<string, unknown>): boolean {
    const module = this.instanceModule();
    if (!module) {
      // Zusterpad van de ontbrekende Uploads-module hierboven: ook dit mag niet stil zijn.
      this.#utilityService.notify(MISSING_RECORDS_MODULE_MESSAGE);
      return false;
    }
    if (this.isDisabled()) return false;

    const existing = row.uploaded;
    const persistence = existing?.id
      ? {
          mode: 'update' as const,
          entryId: existing.id,
          fixedEntryValues: documentReplaceResetValues(
            this.resolveOptionBySlug('status', 'pending', { name: 'In behandeling' })
          ),
        }
      : undefined;

    const result = this.#modalService.resolveCreateRunner(module, {
      prefill: this.#buildPrefill(row, bestand),
      persistence,
      fieldRules: this.#fieldRulesForTemplate(row),
    });
    if (result.status === 'fallback') {
      this.#utilityService.notify(NO_CREATE_FORM_MESSAGE);
      return false;
    }
    // 'denied' meldt ModalService zelf; hier telt alleen dat de runner niet opende.
    if (result.status === 'denied') return false;
    // De runner schrijft server-side; SignalR kan achterlopen op een net-gesloten modal, dus
    // herlaad expliciet zodat de kaart direct in zijn nieuwe staat springt. Via `runnerDialogRef`
    // geldt dat ook voor 'pending' — een klik tijdens een store-reload verloor die verversing
    // anders stilletjes (PR-review P2).
    runnerDialogRef(result)
      .pipe(
        switchMap(ref => ref.afterClosed()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(result => {
        this.#markDocumentsEdited(result, existing?.id);
        this.refreshUploaded();
        if (bestand?.['id']) this.#warnIfOrphaned(String(bestand['id']));
      });
    return true;
  }

  /**
   * Rolt de zojuist opgeslagen bestandsopslag terug wanneer de runner niet opende. Zonder dit
   * bleef er per poging een ongekoppelde Uploads-entry achter (PR-review Joshua, P2). Mislukt het
   * verwijderen zelf, dan blijft alleen de logregel over — dat is nog altijd beter dan stilte.
   */
  #discardOrphanedUpload(uploadsModuleId: string, bakEntryId: string | undefined): void {
    if (!bakEntryId) return;
    this.#entriesService
      .delete(uploadsModuleId, bakEntryId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: err => {
          console.error('Opruimen van de ongekoppelde upload mislukt:', err);
          console.warn(`Ongekoppelde Uploads-entry ${bakEntryId} vereist opschoning.`);
        },
      });
  }

  /**
   * Na het sluiten van de runner met een vooraf opgeslagen sleepzone-bestand: vraag de server of
   * er inmiddels een record aan hangt. Bewust géén store-check — de lokale store loopt via
   * SignalR achter op een net-gesloten modal, waardoor een geslaagde save ten onrechte als
   * afgebroken zou loggen.
   */
  #warnIfOrphaned(bestandId: string): void {
    const moduleId = this.instanceModule()?.id;
    if (!moduleId) return;
    this.#entriesService
      .getEntries({ moduleId, offset: 0, limit: 1, filter: `entry.bestand.id eq '${bestandId}'` })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(records => {
        if (!records?.length) {
          console.warn(
            `Aanmaakformulier gesloten zonder opslaan; ongekoppelde Uploads-entry ${bestandId} vereist opschoning.`
          );
        }
      });
  }

  /**
   * Prefill voor de runner. Bewust ZONDER `bestand`: vervangen vereist een nieuw bestand, en het
   * bestaande meesturen zou de required-validator bevredigen — een directe save zou dan de
   * beoordeling resetten terwijl hetzelfde bestand gekoppeld blijft (zelfde afweging als
   * `documents-cards.#buildPrefill`). Prefill is formstate; annuleren laat het record ongemoeid.
   */
  #buildPrefill(row: ChecklistRow, bestand?: Record<string, unknown>): Entry {
    const template = row.template;
    const existing = row.uploaded;
    const existingEntry: Record<string, unknown> = { ...(existing?.entry ?? {}) };
    delete existingEntry['bestand'];
    const sourceFieldKey = this.unwrapKeyPath(SOURCE_FIELD);
    const anchor = this.#resolveAnchorRefs();

    return {
      id: existing?.id,
      entry: {
        ...existingEntry,
        archived: false,
        // Alleen een NIEUW record krijgt het anker toegewezen; een vervanging houdt de eigenaar,
        // het bedrijf en de locatie van het bestaande record. `asset` is conditional, net als in
        // `bakData` hierboven — geen `asset: null`-schrijving op een company/user-upload.
        ...(existing
          ? {}
          : {
              user: anchor.user,
              company: anchor.company,
              location: anchor.location,
              ...(anchor.asset ? { asset: anchor.asset } : {}),
            }),
        name: template.entry?.['name'] ?? null,
        description: template.entry?.['description'] ?? '',
        hoofdtype: template.entry?.['hoofdtype'] ?? null,
        [sourceFieldKey]: {
          id: template.id,
          name: template.entry?.['name'],
          selectedInput: template.entry?.['name'],
        },
        // Snapshot van de HUIDIGE templatetermijn, ook bij vervangen (#20724).
        'notification-lead-time': documentNotificationLeadTime(template.entry ?? null),
        // Alleen het via de sleepzone net-geüploade bestand komt hier terug; het bestaande
        // bestand blijft bewust weg (zie de doc-comment hierboven).
        ...(bestand ? { bestand } : {}),
      },
    } as Entry;
  }

  /**
   * Company/user/asset/location waar een nieuw document aan hangt. De onboarding-host draagt
   * company/user zelf (`entry.company` / `entry.user`); een bedrijfs-, gebruikers- of
   * bedrijfsmiddel-detail IS het anker; anders valt het terug op de sessie-identiteit.
   * Overgenomen uit het verwijderde directe uploadpad, want een admin kan de onboarding van
   * iemand anders invullen — sessie-identiteit alleen is daar fout.
   *
   * `asset` is special-cased vóór de nested-company/user-lookup: een bedrijfsmiddel kan zijn
   * eigenaar onder een ander pad dragen (bv. `entry.general.company`) zonder dat dit de checklist
   * naar die eigenaar mag laten afdwalen. `company`/`user` behouden hun bestaande precedentie —
   * een host met een eigen `entry.company`/`entry.user` blijft de onboarding-aanmelding, ook als
   * `moduleLinkedField` toevallig `'entry.company'` is (de wizard-eigen configuratie).
   */
  #resolveAnchorRefs(): { user: Entry | null; company: Entry | null; location: Entry | null; asset: Entry | null } {
    const host = this.anchorEntry();
    const backRefKey = this.unwrapKeyPath(this.resolvedBackRefPath());
    let userRef: Entry | null = null;
    let companyRef: Entry | null = null;
    let assetRef: Entry | null = null;

    if (backRefKey === 'asset' && host) {
      assetRef = host;
    } else {
      userRef = (host?.entry?.['user'] as Entry | undefined) ?? null;
      companyRef = (host?.entry?.['company'] as Entry | undefined) ?? null;
      if (!userRef && !companyRef && host) {
        if (backRefKey === 'company') companyRef = host;
        else if (backRefKey === 'user') userRef = host;
      }
    }
    // Session-identity fallback only applies when nothing — including an asset host — already
    // resolved an anchor; an asset-anchored upload must not also pick up the current user/company.
    if (!userRef && !companyRef && !assetRef) {
      userRef = this.#userStore.currentUserEntry();
      companyRef = this.#userStore.currentCompanyEntry();
    }
    const location = (host?.entry?.['location'] as Entry | undefined) ?? this.#userStore.currentLocationEntry();
    return { user: userRef, company: companyRef, location, asset: assetRef };
  }

  // ── Kaart-mappers (app-document-card) ────────────────────────────────────

  /** Instructietekst hoort bij een nog-lege kaart; na upload neemt het record de kaart over. */
  cardDescription(row: ChecklistRow): string | null {
    if (row.uploaded) return null;
    return (row.template.entry?.['description'] as string | undefined) ?? null;
  }

  cardReviewComment(row: ChecklistRow): string | null {
    return (row.uploaded?.entry?.['review-comment'] as string | undefined) ?? null;
  }

  cardFile(row: ChecklistRow): DocumentCardFile | null {
    const uploaded = row.uploaded;
    if (!uploaded || !entryHasFile(uploaded)) return null;
    return {
      name: this.rowFileName(row) ?? (row.template.entry?.['name'] as string | undefined) ?? '',
      uploadedAt: uploaded.createdAt ?? null,
    };
  }

  /**
   * Versiehistorie en archiveren blijven beheer-acties (zie `isManagementSurface`): wie onboardt
   * uploadt en vervangt, maar beheert geen versies. "Voorbeeld downloaden" hangt aan de template
   * en staat er dus ook op een kaart zónder record — juist dan heb je het voorbeeld nodig.
   */
  /**
   * De kaart toont altijd een badge, ook "Nog te uploaden". In de regelweergave verschijnt hij pas
   * zodra er een document ligt: 3802:165013 zet de badge uit op elke lege regel en 3802:165747
   * zet hem aan zodra er geüpload is.
   */
  showCardBadge(row: ChecklistRow): boolean {
    return this.isRowVariant() ? !!row.uploaded : true;
  }

  cardMenu(row: ChecklistRow): DocumentCardMenu {
    // Het onboarding-ontwerp toont geen ...-menu op een regel; alle vervolgacties lopen via de
    // upload-modal. Bewuste keuze (contract-afwijkingslog A5), geen omissie: bekijken,
    // downloaden, archiveren en "Voorbeeld downloaden" zijn in die weergave niet bereikbaar.
    if (this.isRowVariant()) return {};
    const uploaded = row.uploaded;
    const hasFile = !!uploaded && entryHasFile(uploaded);
    return {
      preview: hasFile,
      download: hasFile,
      history: !!uploaded && this.isManagementSurface(),
      archive: !!uploaded && !this.isDisabled() && this.canArchive(),
      replace: !!uploaded && !this.isDisabled() && this.canReplace(uploaded),
      complete: !!uploaded && !this.isDisabled() && !this.rowMetadataComplete(row) && this.canEditRecords(),
      example: !!this.templateFile(row.template),
    };
  }

  /**
   * Nog niets geüpload → sleepzone (één gebaar, het aanmaakformulier opent er meteen achteraan
   * met het bestand al gekoppeld). Vervangen loopt wél via de knop: daar mag het bestaande bestand
   * niet meelopen in de prefill, dus kiest de gebruiker het nieuwe bestand ín het formulier.
   */
  cardUploadMode(row: ChecklistRow): DocumentCardUploadMode {
    // No dropzone in the read view; with no primary action either, the slot renders nothing.
    if (this.isReadView()) return 'button';
    // De regelweergave kent geen sleepzone: het ontwerp (3802:165013) zet op elke lege regel
    // dezelfde Upload-knop, die de upload-modal opent.
    if (this.isRowVariant()) return 'button';
    return row.uploaded || this.isDisabled() ? 'button' : 'dropzone';
  }

  /** Accepted types come from the template when configured, else from the field-level prop. */
  rowAcceptedTypes(row: ChecklistRow): string[] {
    return this.#acceptedTypesFor(row.template);
  }

  #acceptedTypesFor(template: Entry): string[] {
    // Both levels go through normalizeAcceptedTypes: the form builder stores an unconfigured
    // acceptedTypes as an empty string, which a `?? []` guard passes straight through.
    const perTemplate = normalizeAcceptedTypes(template.entry?.['acceptedTypes']);
    if (perTemplate.length) return perTemplate;
    return normalizeAcceptedTypes((this.props as RequiredDocumentsChecklistProps).acceptedTypes);
  }

  /** Field-level size ceiling in MB; falls back to the shared default (upload-validation.util.ts). */
  #resolvedMaxFileSizeMb(): number {
    return resolveMaxFileSizeMb((this.props as RequiredDocumentsChecklistProps).maxFileSizeMb);
  }

  /** Names both the accepted types AND the size ceiling, like `dropzoneHint()` on the plain upload field. */
  rowHint(row: ChecklistRow): string {
    const types = this.rowAcceptedTypes(row);
    const kinds = types.length ? types.join(', ') : 'Alle bestandstypen';
    return `${kinds}, tot ${this.#resolvedMaxFileSizeMb()} MB`;
  }

  /**
   * The notice the HOST published for this record ({@link WizardHostContext.documentNotices}) —
   * typically a reviewer's reason for asking it again. Rendered verbatim and read-only; this field
   * type is generic (company and user document pages run it too), so it owns no opinion about what
   * a notice means or when one applies. A wizard with no host context gets no notices, which is
   * what every non-onboarding surface sees.
   */
  rowFlagNotice(row: ChecklistRow): string | null {
    const recordId = row.uploaded?.id;
    if (!recordId) return null;
    // The host's notices come from the last review it read; that snapshot cannot know about a
    // replacement made seconds ago in this session. Leaving the old reason under a fresh file reads
    // as a verdict on a document no reviewer has seen yet.
    if (this.#recordsEditedThisSession().has(recordId)) return null;
    const notices = this.#hostContext?.documentNotices();
    const notice = notices?.[recordId]?.trim();
    return notice ? notice : null;
  }

  /**
   * The record's OWN rejection reason (`review-comment`), independent of the onboarding entry's
   * flags/status. `rowFlagNotice` only has something to say while the onboarding entry itself is
   * `afgekeurd` — the moment it is resubmitted or otherwise moves on, `readCarrierFlags` goes
   * empty even though the record it flagged is still sitting there `rejected`. The record's own
   * comment has no such expiry, so it is the notice that survives a resubmit.
   */
  #rowReviewComment(row: ChecklistRow): string | null {
    if (!this.#rowIsRejected(row)) return null;
    const raw = row.uploaded?.entry?.['review-comment'] as string | undefined;
    const trimmed = raw?.trim();
    return trimmed ? trimmed : null;
  }

  /** Joins notice sentences with ". ", trimming a redundant trailing period off everything but the last. */
  #joinNotices(...parts: (string | null)[]): string | null {
    const filtered = parts.filter((part): part is string => !!part);
    if (!filtered.length) return null;
    return filtered.map((part, i) => (i < filtered.length - 1 ? part.replace(/\.$/, '') : part)).join('. ');
  }

  /**
   * Het aanmaakformulier markeert `end-date`/`document-number` niet per template als verplicht —
   * dat weet alleen de template. Zonder deze melding zou een gebruiker een document uploaden, de
   * wizard-validator zien blokkeren en nergens lezen wát er ontbreekt.
   *
   * The record's own review-comment leads — it is the reviewer's actual verdict on THIS document
   * and it outlives a resubmit (see `#rowReviewComment`) — then the host's notice when it says
   * something the comment doesn't, then the metadata reminder, all in the same sentence.
   *
   * ONE slot per variant for that comment: the card renders it in its own `[reviewComment]` callout
   * (`cardReviewComment`), so folding it in here printed the same sentence twice on one card. The
   * row variant has no such callout (document-card.component.ts renders only the notice there), so
   * there the notice is the single place the reason can reach the user at all.
   */
  cardNotice(row: ChecklistRow): string | null {
    const reviewComment = this.#rowReviewComment(row);
    const flagNotice = this.rowFlagNotice(row);
    // De-duplicate against whichever review-comment THIS variant actually renders in its own
    // callout: the row variant shows the gated `reviewComment` (null unless rejected) inline above,
    // but the card variant's `[reviewComment]` callout is the UNGATED `cardReviewComment` — deduping
    // the card path against the gated value left a non-rejected record's comment shown twice.
    const dedupeAgainst = this.isRowVariant() ? reviewComment : this.cardReviewComment(row);
    const rejectionNotice = this.#joinNotices(
      this.isRowVariant() ? reviewComment : null,
      flagNotice && flagNotice !== dedupeAgainst ? flagNotice : null
    );
    const ontbreekt = this.#missingMetadataFields(row);
    if (!ontbreekt.length) return rejectionNotice;
    const laatste = ontbreekt[ontbreekt.length - 1];
    const lijst = ontbreekt.length === 1 ? laatste : `${ontbreekt.slice(0, -1).join(', ')} en ${laatste}`;
    const metadata = `Vul nog ${lijst} in via "Gegevens aanvullen".`;
    return this.#joinNotices(rejectionNotice, metadata);
  }

  canEditRecords(): boolean {
    const moduleId = this.instanceModule()?.id;
    return !!moduleId && this.#userStore.checkIfHasEditPermission(moduleId);
  }

  /**
   * Heropent het aanmaakformulier om ontbrekende gegevens aan te vullen. Anders dan bij vervangen
   * blijft het bestaande `bestand` in de prefill staan (er komt geen nieuw bestand) en wordt de
   * beoordeling NIET teruggezet — je vult een datum in, je levert niet opnieuw aan.
   */
  openCompleteRunner(row: ChecklistRow): void {
    const module = this.instanceModule();
    const existing = row.uploaded;
    if (!module || !existing?.id || this.isDisabled()) return;

    const result = this.#modalService.resolveCreateRunner(module, {
      prefill: { id: existing.id, entry: { ...existing.entry } } as Entry,
      persistence: { mode: 'update' as const, entryId: existing.id, fixedEntryValues: {} },
      // "Gegevens aanvullen" opens the same form, so the same requirements apply here: exactly the
      // fields the card asks for are required, the rest stays out of view.
      fieldRules: this.#fieldRulesForTemplate(row),
    });
    if (result.status === 'fallback') {
      this.#utilityService.notify(NO_CREATE_FORM_MESSAGE);
      return;
    }
    runnerDialogRef(result)
      .pipe(
        switchMap(ref => ref.afterClosed()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(result => {
        // "Gegevens aanvullen" writes metadata on the record, which is a user edit just like an
        // upload is — the wizard's own validator gates on it (`allMetadataFilled`).
        this.#markDocumentsEdited(result, existing.id);
        this.refreshUploaded();
      });
  }

  /**
   * De regel heeft precies één knop waar de kaart een knop plus een menu heeft. Figma toont twee
   * standen: "Upload" op een leeg slot en "Edit details" zodra er een document ligt — dat laatste
   * is de bestaande `complete`-intent ("Gegevens aanvullen"). Verlopen, bijna verlopen en
   * afgekeurde documenten staan niet in het ontwerp; die houden hun eigen woord op dezelfde knop,
   * omdat "Gegevens aanvullen" daar het verkeerde aanbod zou zijn.
   */
  private rowPrimaryAction(row: ChecklistRow): DocumentCardPrimaryAction {
    const uploaded = row.uploaded;
    if (!uploaded) return 'upload';
    if (this.canReplace(uploaded)) {
      const badge = this.rowBadge(row).badge;
      if (badge === 'expired' || badge === 'expiring') return 'replace';
      if (badge === 'rejected') return 'retry';
    }
    return !this.isDisabled() && this.canEditRecords() ? 'complete' : null;
  }

  cardPrimaryAction(row: ChecklistRow): DocumentCardPrimaryAction {
    // A read view shows what is there and what is missing; it never offers upload, replace or retry.
    if (this.isReadView()) return null;
    if (this.isRowVariant()) return this.rowPrimaryAction(row);
    const uploaded = row.uploaded;
    // De sleepzone vervangt de knop op een lege kaart; disabled valt terug op een inerte knop.
    if (!uploaded) return this.isDisabled() ? 'upload' : null;
    if (!this.canReplace(uploaded)) return null;
    const badge = this.rowBadge(row).badge;
    if (badge === 'expired' || badge === 'expiring') return 'replace';
    if (badge === 'rejected') return 'retry';
    return null;
  }

  // ── Rij-acties ───────────────────────────────────────────────────────────

  previewRow(row: ChecklistRow): void {
    if (row.uploaded) this.previewInstance(row.uploaded, this.rowTitle(row));
  }

  downloadRow(row: ChecklistRow): void {
    if (row.uploaded) this.downloadInstance(row.uploaded);
  }

  historyRow(row: ChecklistRow): void {
    if (row.uploaded) this.openHistory(row.uploaded, this.rowTitle(row));
  }

  archiveRow(row: ChecklistRow): void {
    if (row.uploaded) this.archiveDocument(row.uploaded);
  }

  // ── Download helpers ─────────────────────────────────────────────────────

  downloadTemplate(template: Entry) {
    this.downloadFromEntry(template, this.templateModule()?.id);
  }

  rowTitle(row: { template: Entry; uploaded?: Entry | null }): string {
    return resolveDocumentTitle(row.uploaded ?? null, row.template);
  }

  previewInstance(uploaded: Entry, title: string): void {
    this.#documentActions.preview(uploaded, title, this.instanceModule()?.id);
  }

  openHistory(uploaded: Entry, title: string): void {
    const moduleId = this.instanceModule()?.id;
    if (!moduleId || !uploaded.id) return;
    openHistoryDialog(this.#dialog, { moduleId, entryId: uploaded.id, title });
  }

  archiveDocument(uploaded: Entry): void {
    const entity = this.instanceModule();
    if (!entity || !uploaded.id || !this.canArchive()) return;
    this.#entriesService
      .confirmArchive(uploaded, entity)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(outcome => {
        // Archiving takes a document OUT of the checklist, which can turn a complete step incomplete
        // again — a user edit exactly like uploading one, and the same reason the host must be told
        // (#21303). The cancelled branch changed nothing, so it must stay pristine.
        if (outcome !== 'confirmed') return;
        this.#markDocumentsEdited(outcome, uploaded.id);
      });
  }

  canArchive(): boolean {
    const moduleId = this.instanceModule()?.id;
    return !!moduleId && this.#userStore.checkIfHasCreatePermission(moduleId);
  }

  downloadInstance(uploaded: Entry) {
    this.#documentActions.download(uploaded, this.instanceModule()?.id);
  }

  private downloadFromEntry(entry: Entry, moduleId: string | undefined) {
    if (!moduleId) return;
    const meta = this.fileMeta(entry);
    if (!meta) {
      this.#utilityService.notify('Geen bestand beschikbaar voor download.');
      return;
    }
    this.#modulesStore.downloadDocument({
      moduleId,
      entryId: entry.id,
      documentId: meta.documentId,
      documentName: meta.documentName,
      blobUrl: meta.blobUrl,
    });
  }

  private fileMeta(entry: Entry): { documentId?: string; documentName?: string; blobUrl?: string } | null {
    return resolveInlineFileMeta(entry);
  }

  // ── Pending-count integration ────────────────────────────────────────────

  private notifyPendingCount(): void {
    const registry = noticeRegistryOf(this.options?.formState);
    if (!registry) return;

    // Skip while templates are still loading. Before the first response lands both
    // `templates()` and `uploaded()` read as their empty defaults, so computing off them here would
    // derive total=0 and RETRACT (setNotice(key, null)) whatever notice the page already holds
    // under this key. The registry is page-scoped now (`DetailsService`), so that notice can
    // perfectly well have been published by an EARLIER mount of this same field — a previous
    // visit to this tab — and this would flash it off the moment the tab remounts, only to
    // republish it a tick later once the real answer arrives. Skipping leaves it standing.
    if (this.isLoading()) return;

    // Only REQUIRED templates contribute to the "missing" count — optional
    // templates (ISO certs etc.) without upload are not a compliance gap. A template is required
    // when `entry.optional !== true`. Uses `#visibleTemplates` (role-filtered), not the raw loaded
    // set — a template hidden by target-role filtering can never be uploaded, so it must never
    // count as "missing" either (same set the wizard actually shows in `rows()`).
    const requiredTemplates = this.#visibleTemplates().filter(t => t.entry?.['optional'] !== true);
    const requiredTemplateIds = requiredTemplates.map(t => t.id);
    const sourceFieldKey = this.unwrapKeyPath(SOURCE_FIELD);

    const matchedTemplateIds = new Set(
      this.uploaded()
        .map(u => u.entry?.[sourceFieldKey]?.id)
        .filter((id): id is string => !!id)
    );
    const missingCount = requiredTemplateIds.filter(id => !matchedTemplateIds.has(id)).length;

    // Hover-card lines for the detail-header notice chip (#21239-follow-up): one per missing
    // required template ("ontbreekt") plus one per uploaded-but-blocking row, using rowPresentation
    // — the SAME derivation `pendingFromUploads` below counts with — so this list can never
    // disagree with the total. Reuses document-card-status.ts's own vocabulary (documentReviewLabel
    // / documentValidityLabel) rather than inventing a second set of Dutch labels.
    const missingItems = requiredTemplates
      .filter(t => !matchedTemplateIds.has(t.id))
      .map(t => `${t.entry?.['name'] ?? ''} — ontbreekt`);

    // pendingFromUploads is scoped to `rows()` (required + optional), NOT to `this.uploaded()`
    // directly. `this.uploaded()` is scoped only by the company/user/asset anchor, so it also
    // contains uploads for templates outside `#visibleTemplates()` — e.g. vehicle documents
    // (`entityType: 'asset'`) filtered out by this checklist's `templateFilter`, or a template
    // hidden by target-role filtering. Those uploads belong to a different checklist's business
    // and must never inflate this one's count, even though the onboarder/reviewer can never see or
    // act on them here. Uses `rowBlocksOnboarding()` — the same predicate the checklist itself
    // paints rows with — so "pending" here always matches what is visibly highlighted per row.
    // `row.uploaded != null`, not `entryHasFile(row.uploaded)`: a MATCHED record with no file still
    // blocks (`deriveDocumentPresentation`'s `hasFile === false` branch) and must still be counted
    // — `entryHasFile` here only ever excluded genuinely unmatched rows, which `rowBlocksOnboarding`
    // already handles (reviewState 'missing' !== 'approved'), so the file check added nothing but a
    // silent undercount for the "matched, no file" case.
    const pendingRows = this.rows().filter(row => row.uploaded != null && this.rowBlocksOnboarding(row));
    const pendingFromUploads = pendingRows.length;
    const total = missingCount + pendingFromUploads;

    const pendingItems = pendingRows.map(row => `${row.template.entry?.['name'] ?? ''} — ${this.#pendingReason(row)}`);
    const items = [...missingItems, ...pendingItems];

    debugLog(
      'OnboardingDocs',
      `checklist: ${this.#visibleTemplates().length} templates (${requiredTemplateIds.length} required), ${this.uploaded().length} uploads, missing-required=${missingCount}, pending-from-uploads=${pendingFromUploads}, total=${total}`
    );
    // The Dutch wording is OURS, not DetailsService's — the registry only stores/sorts notices,
    // it does not know what a "pending documents" count means.
    // The key is derived from the field on every call (`noticeSourceKeyForField`), which is what
    // makes a second mount of this SAME field (the Overzicht own-view card) collapse onto the same
    // registry entry instead of fighting the first.
    registry.setNotice(
      noticeSourceKeyForField('required-documents-checklist', this.field),
      total > 0
        ? { severity: NoticeSeverityEnum.Warning, label: `${total} document(en) wachten`, items, count: total }
        : null
    );
  }

  /**
   * The reason a blocking, uploaded row still counts as pending, in the SAME Dutch vocabulary the
   * cards already show (document-card-status.ts) — never a third set of labels for the same
   * states. Precedence, most specific first:
   *   1. no file at all ("geen bestand") — a matched record with nothing attached yet;
   *   2. expiry ("verlopen") — an approved-but-expired document;
   *   3. approved but missing required metadata ("gegevens onvolledig") — `reviewState` alone would
   *      read "goedgekeurd" here, which is exactly the misleading label a "still pending" line must
   *      not show;
   *   4. the plain review-state label otherwise.
   */
  #pendingReason(row: ChecklistRow): string {
    if (!entryHasFile(row.uploaded)) {
      return 'geen bestand';
    }
    const presentation = this.rowPresentation(row);
    if (presentation.validityState === 'expired') {
      return (documentValidityLabel(presentation) ?? 'verlopen').toLowerCase();
    }
    if (presentation.reviewState === 'approved' && !this.rowMetadataComplete(row)) {
      return 'gegevens onvolledig';
    }
    return documentReviewLabel(presentation.reviewState).toLowerCase();
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private unwrapKeyPath(keyPath: string): string {
    return keyPath.replace(/^entry\./, '');
  }

  /**
   * Thin wrapper around the shared FormHelper resolver so checklist and
   * documents-cards use one implementation (option ids are environment-
   * generated — always resolved at runtime, never hardcoded).
   */
  private resolveOptionBySlug(
    fieldKey: string,
    slug: string,
    fallback: { name?: string } = {}
  ): { id?: string; name?: string; slug: string; selectedInput?: string } {
    const moduleId = this.instanceModule()?.id;
    return FormHelper.resolveOptionBySlug(
      fieldKey,
      slug,
      {
        moduleId,
        generatedForm: moduleId ? this.#modulesStore.generatedForms()[moduleId] : null,
        taxonomies: this.#modulesStore.taxonomies(),
        moduleCodeToId: code => this.#modulesStore.moduleCodeToId(code),
      },
      fallback
    );
  }
}
